import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ErrorBanner from '../../../components/ErrorBanner';
import { useAuth } from '../../../context/AuthContext';
import { setPreviewMode } from '../utils/adminPreview';
import styles from './AdminLoginPage.module.css';

const ADMIN_REMEMBER_KEY = 'policyora_admin_remembered_identifier';

const getRememberedAdminValue = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(ADMIN_REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
};

const setRememberedAdminValue = (value: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(ADMIN_REMEMBER_KEY, value);
    } else {
      window.localStorage.removeItem(ADMIN_REMEMBER_KEY);
    }
  } catch {
    // ignore storage failures
  }
};

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [email, setEmail] = React.useState(() => getRememberedAdminValue());
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(() => Boolean(getRememberedAdminValue()));
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [emailError, setEmailError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');

  const validate = () => {
    let hasError = false;
    setEmailError('');
    setPasswordError('');

    if (!email.trim()) {
      setEmailError('Email address is required');
      hasError = true;
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setEmailError('Enter a valid email address');
      hasError = true;
    }

    if (!password) {
      setPasswordError('Password is required');
      hasError = true;
    }

    return !hasError;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validate()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('username', email.trim());
      formData.append('password', password);

      const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
      const response = await fetch(`${baseUrl}/api/v1/auth/admin-login`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || 'Admin login failed');
      }

      const data = await response.json();
      await loginWithToken(
        data.access_token,
        JSON.stringify({
          username: data.cognito_username || data.user.email,
          email: data.user.email,
          is_admin: true,
        }),
        data.user,
      );

      if (rememberMe) {
        setRememberedAdminValue(email.trim());
      } else {
        setRememberedAdminValue(null);
      }

      setPreviewMode('admin');
      navigate('/admin/dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      {/* Theme toggle */}
      <div className={styles.themeToggleContainer}>
      </div>

      {/* Wave background band */}
      <div className={styles.topBand}>
        <svg viewBox="0 0 1440 420" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width: '100%', display: 'block' }}>
          <path d="M0,0 L1440,0 L1440,340 Q1080,420 720,380 Q360,340 0,400 Z" fill="#101828" />
        </svg>
      </div>

      {/* Logo */}
      <div className={styles.topLogo}>
        <div className={styles.brandLogoRow}>
          <div className={styles.brandShield}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          </div>
          <span className={styles.brandLogoText}>Policyora</span>
        </div>
      </div>

      {/* Card */}
      <div className={styles.cardWrapper}>
        <div className={styles.card}>
          <p className={styles.formSubtitle}>Please login to admin dashboard</p>

          {error && (
            <ErrorBanner
              variant="inline"
              align="center"
              message={error}
              details="Please double-check your credentials and try again."
            />
          )}

          <form onSubmit={handleSubmit} noValidate autoComplete="on">
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="admin-email">Email/Username*</label>
              <div className={styles.inputWrapper}>
                <input
                  id="admin-email"
                  name="username"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder="admin@example.com"
                  autoComplete="username"
                  required
                  disabled={submitting}
                />
              </div>
              {emailError && <div className={styles.fieldError}>{emailError}</div>}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="admin-password">Password*</label>
              <div className={styles.inputWrapper}>
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={styles.input}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(p => !p)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M3 3l18 18"/><path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-5.12"/>
                      <path d="M10.73 5.08A10.21 10.21 0 0 1 12 5c5 0 9.27 3.11 11 7.5a13.16 13.16 0 0 1-2.17 3.18"/>
                      <path d="M6.61 6.61C4.07 8.07 2.24 10.43 1 12.5c1.73 4.39 6 7.5 11 7.5 1.27 0 2.5-.19 3.64-.54"/>
                    </svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && <div className={styles.fieldError}>{passwordError}</div>}
            </div>

            <div className={styles.optionsRow}>
              <label className={styles.rememberMe}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => {
                    const checked = e.target.checked;
                    setRememberMe(checked);
                    if (!checked) {
                      setRememberedAdminValue(null);
                    }
                  }}
                />
                <span>Remember me</span>
              </label>
              <button type="button" className={styles.linkButton}>Forgot password?</button>
            </div>

            <button className={styles.submit} type="submit" disabled={submitting}>
              {submitting ? 'Logging in…' : 'Login'}
            </button>
          </form>

          <div className={styles.footerCta}>
            <span>Not an admin?</span>
            <Link to="/login" className={styles.footerLink}>Back to user login</Link>
          </div>
        </div>

      </div>
    </div>
  );
}
